import { cx } from "@suse-o11y/class-variance";
import { IconChevronDown, IconGear } from "@suse-o11y/icons";
import React from "react";
import { useSelector } from "react-redux";

import { Checkbox } from "~/components/checkbox";
import { Dropdown } from "~/components/dropdown";
import { FormField } from "~/components/form/field";

import { selectAllSearchParams } from "../../../../app/state/route/RouteSelectors";
import { selectTimeRange } from "../../../../app/state/view/timeline/TimelineSelectors";
import { Widget, WidgetBody, WidgetHeader } from "../../../../components/_widget";
import { useNavigateFrom } from "../../../../components/react/link";

import type { SettingsFormState } from "./settings-form-state";

interface SettingsMenuProps {
	settings: SettingsFormState;
}

export const SettingsMenu: React.FC<SettingsMenuProps> = ({ settings }) => (
	<Dropdown
		align={{ targetOffset: [0, 0] }}
		overlay={<SettingsOverlay settings={settings} />}
		overlayClassName={cx("max-h-full")}
		placement="bottomRight"
		trigger={["click"]}
	>
		<button className="button button-tertiary" type="button">
			<IconGear className="mr-1" fill="none" height={13} stroke="currentColor" strokeWidth="1.5" width={13} />
			<IconChevronDown height={13} strokeWidth={0.5} width={13} />
		</button>
	</Dropdown>
);

const SettingsOverlay: React.FC<SettingsMenuProps> = ({ settings }) => {
	const timeRangeFromURL = useSelector(selectTimeRange);
	const { refreshInterval: refreshIntervalFromURL = "off" } = useSelector(selectAllSearchParams);

	const navigateFrom = useNavigateFrom();

	return (
		<div className="w-80 rounded-sm border-grey-300 border-x bg-white py-2 shadow-lg">
			<Widget card>
				<WidgetHeader size="md" title="Saving..." />

				<WidgetBody className="mb-0">
					<div className="text-13 text-grey-800">
						<p className="mb-2 leading-4.5">When saving the dashboard, also include:</p>
						<ul className="mb-0 flex flex-col gap-2">
							<FormField el="li" state={settings.fields.timeRange}>
								{(monitor, field) => {
									const timeRange = field.getInput(monitor) ?? undefined;

									return (
										<SettingsListItem
											label="time window"
											onChange={(checked) => {
												if (checked) {
													field.setInput(timeRangeFromURL);
												} else {
													navigateFrom("*", {
														search: (params) => ({ ...params, timeRange }),
													});
													field.setInput(null);
												}
											}}
											value={timeRange != null}
										/>
									);
								}}
							</FormField>

							<FormField el="li" state={settings.fields.refreshInterval}>
								{(monitor, field) => {
									const refreshInterval = field.getInput(monitor) ?? undefined;

									return (
										<SettingsListItem
											label="data refresh interval"
											onChange={(checked) => {
												if (checked) {
													field.setInput(refreshIntervalFromURL);
												} else {
													navigateFrom("*", {
														search: (params) => ({ ...params, refreshInterval }),
													});
													field.setInput(null);
												}
											}}
											value={refreshInterval != null}
										/>
									);
								}}
							</FormField>

							<FormField el="li" state={settings.fields.saveVariables}>
								{(monitor, field) => {
									const saveVariables = field.getInput(monitor);

									return (
										<SettingsListItem
											label="variables"
											onChange={(checked) => {
												field.setInput(checked);
											}}
											value={saveVariables}
										/>
									);
								}}
							</FormField>
						</ul>
					</div>
				</WidgetBody>
			</Widget>
		</div>
	);
};

interface SettingsListItemProps {
	label: string;
	value: boolean;
	onChange: (newValue: boolean) => void;
}

const SettingsListItem: React.FC<SettingsListItemProps> = ({ label, value, onChange }) => (
	<label className="flex cursor-pointer flex-row items-center gap-2">
		<Checkbox checked={value} onCheckedChange={(checked) => onChange(checked)} />
		<span>{label}</span>
	</label>
);
